import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getBranches } from '../services/branchService';
import { getBranchInventory } from '../services/transferService';
import { formatCurrency } from '../utils/format';
import AjusteProductoModal from '../components/AjusteProductoModal';

function groupByProduct(inventory) {
  const map = new Map();
  inventory.forEach(item => {
    const pid = item.product?.id;
    if (!pid) return;
    if (!map.has(pid)) {
      map.set(pid, {
        productId: pid,
        product: item.product,
        totalStock: 0,
        deposits: [],
      });
    }
    const group = map.get(pid);
    group.totalStock += item.stock || 0;
    group.deposits.push({
      inventoryId: item.id,
      depositId: item.deposit?.id,
      depositName: item.deposit?.name,
      main: item.deposit?.main,
      stock: item.stock,
      stockMin: item.stockMin,
      stockOrder: item.stockOrder,
    });
  });
  return Array.from(map.values());
}

export default function Ajustes() {
  const [branches, setBranches] = useState([]);
  const [loadingBranches, setLoadingBranches] = useState(true);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [editingItem, setEditingItem] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await getBranches();
        setBranches((Array.isArray(data) ? data : []).filter(b => b.state));
      } catch {
        setError('Error al cargar las sucursales');
      } finally {
        setLoadingBranches(false);
      }
    })();
  }, []);

  const fetchInventory = useCallback(async (branchId) => {
    if (!branchId) {
      setInventory([]);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await getBranchInventory(branchId);
      setInventory(Array.isArray(data) ? data : []);
    } catch {
      setError('No se pudo cargar el inventario de la sucursal.');
      setInventory([]);
    } finally {
      setLoading(false);
    }
  }, []);

  function handleBranchChange(value) {
    setSelectedBranch(value);
    setQuery('');
    fetchInventory(value);
  }

  const branchName = branches.find(b => String(b.id) === String(selectedBranch))?.name || '';

  const grouped = useMemo(() => groupByProduct(inventory), [inventory]);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return grouped;
    return grouped.filter(g => g.product.name.toLowerCase().includes(q));
  }, [grouped, query]);

  function handleSaved() {
    setEditingItem(null);
    fetchInventory(selectedBranch);
  }

  return (
    <div className="page-fade" style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ marginBottom: 22 }}>
        <p style={{ fontSize: 12, color: 'var(--orig-text-muted)', marginBottom: 3 }}>
          Ajuste de costo, precio e inventario por sucursal
        </p>
        <h5 style={{
          fontWeight: 800, color: 'var(--orig-text)',
          fontFamily: 'var(--orig-font-display)', letterSpacing: 0.5, fontSize: 20,
        }}>
          Ajustes
        </h5>
      </div>

      {/* Filters */}
      <div className="section-card" style={{ marginBottom: 20 }}>
        <div className="section-body" style={{ padding: '16px 20px' }}>
          <div className="row g-3 align-items-end">
            <div className="col-12 col-md-5">
              <label className="form-label">Sucursal</label>
              <select
                className="form-select"
                value={selectedBranch}
                onChange={e => handleBranchChange(e.target.value)}
                disabled={loadingBranches}
              >
                <option value="">{loadingBranches ? 'Cargando...' : 'Seleccione una sucursal'}</option>
                {branches.map(b => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>

            <div className="col-12 col-md-5">
              <label className="form-label">Producto</label>
              <div className="search-wrap">
                <i className="bi bi-search search-icon" />
                <input
                  type="text"
                  className="form-control"
                  placeholder="Buscar producto..."
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  disabled={!selectedBranch}
                />
                {query && (
                  <button className="clear-btn" onClick={() => setQuery('')} title="Limpiar">
                    <i className="bi bi-x-lg" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="alert-orig alert-error" style={{ marginBottom: 20 }}>
          <i className="bi bi-exclamation-triangle-fill" />
          {error}
        </div>
      )}

      {/* Results */}
      {!selectedBranch ? (
        <div className="section-card">
          <div style={{ padding: '50px 20px', textAlign: 'center', color: 'var(--orig-text-muted)' }}>
            <i className="bi bi-building" style={{ fontSize: 36, display: 'block', marginBottom: 12, opacity: 0.4 }} />
            <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>Seleccione una sucursal</div>
            <div style={{ fontSize: 12 }}>Elija una sucursal para ajustar su costo, precio e inventario.</div>
          </div>
        </div>
      ) : loading ? (
        <div className="section-card">
          <div style={{ padding: '50px 20px', textAlign: 'center', color: 'var(--orig-text-muted)' }}>
            <div className="spinner" style={{ margin: '0 auto 14px' }} />
            <div style={{ fontSize: 13 }}>Cargando inventario...</div>
          </div>
        </div>
      ) : (
        <div className="section-card">
          <div className="section-header">
            <h6>
              <i className="bi bi-sliders" style={{ color: 'var(--orig-green)' }} />
              Productos — {branchName}
            </h6>
            <span className="badge-orig badge-green">
              {filtered.length} {filtered.length === 1 ? 'producto' : 'productos'}
            </span>
          </div>

          <div style={{ overflowX: 'auto' }}>
            {filtered.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: 'var(--orig-text-muted)' }}>
                <i className="bi bi-search" style={{ fontSize: 30, display: 'block', marginBottom: 10, opacity: 0.4 }} />
                <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>Sin resultados</div>
                <div style={{ fontSize: 12 }}>
                  {query ? 'No hay productos que coincidan con la búsqueda.' : 'No hay inventario registrado en esta sucursal.'}
                </div>
              </div>
            ) : (
              <table className="orig-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Producto</th>
                    <th>Costo</th>
                    <th>Precio</th>
                    <th>Stock total</th>
                    <th style={{ textAlign: 'center' }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((item, idx) => (
                    <tr key={item.productId}>
                      <td style={{ color: 'var(--orig-text-muted)', fontSize: 12, fontFamily: 'monospace' }}>
                        {idx + 1}
                      </td>
                      <td>
                        <div style={{ fontWeight: 500 }}>{item.product.name}</div>
                        <div style={{ fontSize: 11, color: 'var(--orig-text-muted)' }}>
                          {item.product.packageType}
                          {item.product.packageQuantity ? ` × ${item.product.packageQuantity}` : ''}
                        </div>
                      </td>
                      <td style={{ fontSize: 12.5, color: 'var(--orig-text-muted)' }}>{formatCurrency(item.product.cost)}</td>
                      <td style={{ fontSize: 12.5, fontWeight: 500 }}>{formatCurrency(item.product.price)}</td>
                      <td style={{ fontSize: 14, fontWeight: 700, color: 'var(--orig-text)' }}>{item.totalStock}</td>
                      <td style={{ textAlign: 'center' }}>
                        <button
                          className="btn-orig btn-orig-outline"
                          onClick={() => setEditingItem({ ...item, branchName })}
                          style={{ padding: '5px 12px', fontSize: 12 }}
                          title="Ajustar producto"
                        >
                          <i className="bi bi-sliders" />
                          Ajustar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {editingItem && (
        <AjusteProductoModal
          item={editingItem}
          onClose={() => setEditingItem(null)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
